import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  forwardRef,
  input,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import intlTelInput, { Iso2, Iti, NUMBER_FORMAT } from 'intl-tel-input';

export interface PhoneNumberValue {
  e164Number: string;
}

@Component({
  selector: 'app-phone-number-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<input
    #phoneInput
    type="tel"
    class="form-control"
    [id]="id()"
    [name]="name()"
    [attr.maxlength]="maxLength()"
  />`,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PhoneNumberInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PhoneNumberInputComponent), multi: true },
  ],
})
export class PhoneNumberInputComponent implements AfterViewInit, OnDestroy, ControlValueAccessor, Validator {
  phoneInputRef = viewChild.required<ElementRef<HTMLInputElement>>('phoneInput');

  preferredCountries = input<string[]>(['za', 'gb', 'us']);
  initialCountry = input('za');
  separateDialCode = input(false);
  maxLength = input(15);
  id = input('phone');
  name = input('phone');

  private iti: Iti;
  private value = '';
  private pendingDisabled = false;
  private onChange: (value: PhoneNumberValue | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;
  private readonly inputListener = () => this.emitValue();
  private readonly blurListener = () => this.onTouched();

  ngAfterViewInit(): void {
    const input = this.phoneInputRef().nativeElement;
    this.iti = intlTelInput(input, {
      initialCountry: this.initialCountry() as Iso2,
      countryOrder: this.preferredCountries() as Iso2[],
      separateDialCode: this.separateDialCode(),
      numberDisplayFormat: 'NATIONAL',
      loadUtils: () => import('intl-tel-input/utils'),
    });

    if (this.value) {
      this.iti.setNumber(this.value);
    }
    this.iti.setDisabled(this.pendingDisabled);

    input.addEventListener('input', this.inputListener);
    input.addEventListener('blur', this.blurListener);

    this.onValidatorChange();
  }

  ngOnDestroy(): void {
    const input = this.getNativeInput();
    input?.removeEventListener('input', this.inputListener);
    input?.removeEventListener('blur', this.blurListener);
    this.iti?.destroy();
  }

  writeValue(value: PhoneNumberValue | string | null): void {
    this.value = typeof value === 'string' ? value : (value?.e164Number ?? '');
    this.iti?.setNumber(this.value);
  }

  registerOnChange(fn: (value: PhoneNumberValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.pendingDisabled = isDisabled;
    this.iti?.setDisabled(isDisabled);
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(): ValidationErrors | null {
    if (!this.iti || !this.value) {
      return null;
    }
    return this.iti.isValidNumber() === false ? { pattern: true } : null;
  }

  private emitValue(): void {
    const e164Number = this.iti.getNumber(NUMBER_FORMAT.E164) || '';
    this.value = e164Number;
    this.onChange(e164Number ? { e164Number } : null);
  }

  /**
   * The view query is `required`, so calling it before the component's view has ever gone
   * through change detection (e.g. `ngOnDestroy` invoked without a prior `detectChanges`) would
   * throw. Guard that lookup so destruction is always safe, matching the previous
   * optional-chaining behaviour on the plain `@ViewChild` reference.
   */
  private getNativeInput(): HTMLInputElement | undefined {
    try {
      return this.phoneInputRef()?.nativeElement;
    } catch {
      return undefined;
    }
  }
}
